import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatINR } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import HealthGauge from "@/components/HealthGauge";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Wallet, TrendingDown, PiggyBank, LineChart, Sparkles, ArrowRight,
  Pencil, Check, AlertTriangle, CheckCircle2, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";

const CHART_COLORS = ["#6366F1", "#0F172A", "#10B981", "#F59E0B", "#38BDF8", "#8B5CF6", "#F472B6"];

function StatCard({ icon: Icon, label, value, sub, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay }}
      className="rounded-3xl border border-slate-200 bg-white p-5 soft-shadow">
      <div className="h-9 w-9 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
        <Icon size={18} strokeWidth={1.75} className="text-slate-700" />
      </div>
      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className="font-mono font-bold text-2xl tracking-tight text-slate-900 mt-1"
        data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </motion.div>
  );
}

const statusColor = { good: "#10B981", ok: "#F59E0B", low: "#F97316" };

export default function Dashboard() {
  const { user, updateIncome } = useAuth();
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [trends, setTrends] = useState([]);
  const [nudges, setNudges] = useState([]);
  const [editIncome, setEditIncome] = useState(false);
  const [incomeVal, setIncomeVal] = useState("");

  const load = async () => {
    const [s, t, n, p] = await Promise.all([
      api.get("/summary"), api.get("/trends"), api.get("/nudges"), api.get("/portfolio"),
    ]);
    setSummary(s.data); setTrends(t.data); setNudges(n.data); setPortfolio(p.data);
    api.get("/health-score").then((h) => setHealth(h.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const saveIncome = async () => {
    await updateIncome(Number(incomeVal) || 0);
    setEditIncome(false);
    toast.success("Income updated");
    load();
  };

  const firstName = (user?.name || "there").split(" ")[0];
  const spending = summary?.expense_by_category || [];

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-sm text-slate-400">Welcome back,</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">{firstName}</h1>
        </div>
        <Link to="/app/invest">
          <Button data-testid="home-invest-cta" className="rounded-full h-11 px-6 gap-2 bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-0.5 transition-transform duration-300">
            <Sparkles size={18} /> Start Investment Plan
          </Button>
        </Link>
      </div>

      {/* Nudges */}
      {nudges.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="nudges-section">
          {nudges.map((n, i) => {
            const positive = n.severity === "positive";
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                data-testid="nudge-item"
                className={`flex items-start gap-3 rounded-2xl border p-4 ${positive ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                {positive ? <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" /> : <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />}
                <div>
                  <p className="font-semibold text-sm text-slate-900">{n.title}</p>
                  <p className="text-sm text-slate-600">{n.message}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Health Score + Invest CTA */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 rounded-3xl border border-slate-200 bg-white p-6 md:p-8 soft-shadow" data-testid="health-score-card">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-5">Financial Health Score</p>
          {!health ? (
            <div className="h-48 flex items-center justify-center text-slate-400 text-sm">Calculating your score…</div>
          ) : (
            <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-center">
              <div className="flex flex-col items-center">
                <HealthGauge score={health.score} />
                <span className="mt-3 px-4 py-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold" data-testid="health-grade">{health.grade}</span>
              </div>
              <div>
                <p className="text-slate-700 leading-relaxed mb-5">{health.summary}</p>
                <div className="space-y-2.5">
                  {health.breakdown.map((b) => (
                    <div key={b.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-500 font-medium">{b.label}</span>
                        <span className="font-mono text-slate-400">{b.score}/{b.max}</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className="h-full rounded-full" style={{ background: statusColor[b.status] }}
                          initial={{ width: 0 }} animate={{ width: `${(b.score / b.max) * 100}%` }} transition={{ duration: 0.8 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {health?.tips?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-100 space-y-2.5">
              {health.tips.map((t, i) => (
                <div key={i} className="flex gap-2.5 text-sm text-slate-600">
                  <Lightbulb size={17} className="text-indigo-500 shrink-0 mt-0.5" /><span>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invest CTA */}
        <div className="rounded-3xl bg-slate-900 p-6 md:p-8 text-white relative overflow-hidden flex flex-col">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-indigo-500/30 blur-3xl" />
          <Sparkles size={26} className="text-indigo-300 mb-4 relative z-10" />
          <h3 className="font-heading font-semibold text-xl mb-2 relative z-10">Not sure where to invest?</h3>
          <p className="text-sm text-slate-300 mb-6 relative z-10 leading-relaxed">
            Answer 4 quick questions. Our AI researches the best-fit options and explains them simply.
          </p>
          <Link to="/app/invest" className="relative z-10 mt-auto">
            <Button data-testid="home-invest-cta-2" className="rounded-full gap-2 bg-white text-slate-900 hover:bg-slate-100 w-full">
              Start My Plan <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </div>

      {/* Income banner */}
      <div className="rounded-3xl border border-slate-200 bg-white p-5 mb-6 flex flex-wrap items-center justify-between gap-4 soft-shadow">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Monthly income</p>
          {editIncome ? (
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" data-testid="income-input" value={incomeVal} onChange={(e) => setIncomeVal(e.target.value)} className="h-10 w-40 font-mono rounded-xl" autoFocus />
              <Button size="icon" data-testid="income-save" onClick={saveIncome} className="h-10 w-10 rounded-xl bg-slate-900"><Check size={16} /></Button>
            </div>
          ) : (
            <p className="font-mono font-bold text-2xl text-slate-900">{formatINR(user?.monthly_income)}</p>
          )}
        </div>
        {!editIncome && (
          <Button variant="ghost" size="sm" data-testid="income-edit" onClick={() => { setIncomeVal(String(user?.monthly_income || "")); setEditIncome(true); }} className="gap-2 rounded-full text-slate-500">
            <Pencil size={15} /> Edit
          </Button>
        )}
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Wallet} label="Income" value={formatINR(summary.total_income)} sub={`${summary.savings_rate}% saved`} delay={0} />
          <StatCard icon={TrendingDown} label="Spends" value={formatINR(summary.total_expenses)} sub={`${summary.transaction_count} txns`} delay={0.05} />
          <StatCard icon={PiggyBank} label="Savings" value={formatINR(summary.savings)} sub="income − spends" delay={0.1} />
          <StatCard icon={LineChart} label="Invested" value={formatINR(portfolio?.total_value || 0)} sub={portfolio ? `${portfolio.returns_pct}% returns` : ""} delay={0.15} />
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 soft-shadow">
          <h3 className="font-heading font-semibold text-lg mb-5">Where your money goes</h3>
          {spending.length === 0 ? (
            <div className="h-56 flex flex-col items-center justify-center text-center text-slate-400">
              <p className="mb-3 text-sm">No expenses logged yet.</p>
              <Link to="/app/activity"><Button variant="outline" className="rounded-full">Add a transaction</Button></Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={spending} dataKey="amount" nameKey="category" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {spending.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {spending.slice(0, 6).map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{c.category}</span>
                    <span className="font-mono font-medium text-slate-900">{formatINR(c.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 soft-shadow" data-testid="trends-section">
          <h3 className="font-heading font-semibold text-lg mb-5">Monthly trends</h3>
          {trends.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-center text-slate-400 text-sm">Add transactions across months to see trends.</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={trends} margin={{ left: -12, right: 4, top: 4 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatINR(v)} cursor={{ fill: "#F8FAFC" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Income" fill="#6366F1" radius={[5, 5, 0, 0]} />
                <Bar dataKey="expense" name="Spends" fill="#F59E0B" radius={[5, 5, 0, 0]} />
                <Bar dataKey="savings" name="Savings" fill="#10B981" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
